import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Grid } from '@giphy/react-components';
import { GiphyFetch } from '@giphy/js-fetch-api';
import useMentionAutocomplete from '../../utils/useMentionAutocomplete';
import useMediaQuery from '../../utils/useMediaQuery';
import useKeyboardOpen from '../../utils/useKeyboardOpen';
import useKeyboardInset from '../../utils/useKeyboardInset';
import { MentionMirror } from '../MentionText';
import EmojiPicker from '../EmojiPicker';
import { useVoiceRecorder, formatDuration, MAX_VOICE_SECONDS } from '../VoiceRecorder';
import { measurePeaks } from '../../utils/audioPeaks';
import linkPreviewService from '../../services/linkPreviewService';
import { notifyTyping } from '../../services/messagesRealtime';
import { discoFrame, fillFor } from '../wall/wallTokens';
import { T, RADIUS, BODY_FONT, EASE, bodySize, VIEW_LIMITS, viewLimitLabel, firstNameOf } from './messagesModel';
import { riseIn } from './messagesMotion';
import ViewLimitIcon from './ViewLimitIcon';
import VideoTrimSheet from './VideoTrimSheet';
import AlbumTray from './AlbumTray';

import { MAX_ALBUM_ITEMS, sortPick, defaultTrim } from './albumModel';
import { formatStamp } from './voiceNoteModel';

// Official Giphy SDK fetch instance — client-side, key is a public/client key. Created once
// at module scope so the singleton is reused across re-renders.
const giphyFetch = new GiphyFetch(process.env.REACT_APP_GIPHY_API_KEY || '');

const URL_REGEX = /https?:\/\/[^\s]+/i;

/**
 * The composer field grows to six lines, then scrolls inside.
 *
 * One constant, used by both the autosize effect and the element's own max-height. They
 * were separate numbers once and drifted apart: the effect capped at 144 while the style
 * capped at 136, so the sixth line was clipped by 8px.
 */
const COMPOSER_LINE_HEIGHT = 20;
const COMPOSER_MAX_LINES = 6;
const COMPOSER_PAD_Y = 16;      // the field's vertical padding, top + bottom
export const COMPOSER_MAX_HEIGHT = COMPOSER_LINE_HEIGHT * COMPOSER_MAX_LINES + COMPOSER_PAD_Y;
/** The same cap for a different line height — a phone's 17px type sits on 22px lines. */
export const composerMaxHeight = (lineHeight) => lineHeight * COMPOSER_MAX_LINES + COMPOSER_PAD_Y;
/** The line a size sits on: 20px under 15px type, 22px under 17px. */
export const composerLineHeight = (fontSize) => (fontSize >= 17 ? 22 : COMPOSER_LINE_HEIGHT);
/** Past this the field has wrapped: its corners go from a pill to 20px. */
export const MULTILINE_ABOVE_PX = 44;

/**
 * Whether pressing Enter in the composer sends the message.
 *
 * On a physical keyboard Enter sends and Shift+Enter breaks the line. A touch keyboard has
 * no Shift+Enter to offer, so there Enter is always a newline and the send button is the
 * only way to send. The test is the pointer, not the screen width.
 */
export function enterSends({ hasPhysicalKeyboard, shiftKey }) {
  if (!hasPhysicalKeyboard) return false;
  return !shiftKey;
}

/**
 * Whether the composer's action row is folded away (phones only).
 *
 * `actionsOpen` is the user asking, explicitly, and it beats every reason to hide. Focusing
 * the field clears it again, because that's the moment the field wants the width back.
 */
export function composerActionsCollapsed({ actionsOpen, focused, hasText, gifOpen, emojiOpen }) {
  if (actionsOpen) return false;
  return Boolean(focused || hasText || gifOpen || emojiOpen);
}

/**
 * The composer (handoff: "Composer" + "Reply bar"). A pill field with the disco frame on
 * focus and the ring as its border while it holds text; four bare actions that fold behind
 * a round chevron on a phone; a round send button that fills black once there is something
 * to send. Sending hands the draft to the outbox and returns its temp id, so the thread can
 * fly the new bubble out of the send button.
 */
export default function Composer({
  threadId,
  onSend,
  members = [],
  replyTo = null,
  onCancelReply,
  sendButtonRef = null,
  /** Plan detection: the calendar-plus action (absent without a handler), whether its popover is up, and a text/no-text signal for the bar. */
  onFindPlan = null,
  findPlanActive = false,
  onDraftChange = null,
  /** A short black pill with a line of mono caps (skipped files, the ten-at-a-time cap). */
  onToast = null,
  /**
   * Voice-note note mode (voice-notes handoff §4): the bar becomes the note field, in place,
   * so it sits on the keyboard like a normal message. `{ stamp, at, senderName, mine, onRepin,
   * onSave(body), onCancel, saving, error }`; null is the ordinary composer.
   */
  noteMode = null,
}) {
  // Writes `html[data-keyboard]`, which is what gives the bar back its safe-area strip the
  // moment the keyboard goes away — focus alone is not that moment on Android.
  useKeyboardOpen();
  const [text, setText] = useState('');
  // The tray (albums, Oct 2026): up to ten of
  // {clientId, kind: 'image'|'video', file, url, name, size, durationSec?, trim?: {startMs,endMs}}.
  // One alone sends as a photo / video message, as before; two or more send as one album.
  const [pendingItems, setPendingItems] = useState([]);
  const pendingImage = pendingItems.length === 1 ? pendingItems[0] : null;
  // A clip's trim sheet, opened from its tile's TRIM chip. A long clip is no longer held
  // hostage: it defaults to its first 15 min and the sheet is one tap away.
  const [trimming, setTrimming] = useState(null);                // the item whose window is being set
  const [error, setError] = useState('');
  const [gifOpen, setGifOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [multiline, setMultiline] = useState(false);
  // How many looks the next message grants: 0 is the ordinary kind, 1 is view once, 2 is
  // view twice (one replay). Words or a photo; a GIF or a voice note ignores it. Back to 0
  // after every send, so it has to be chosen for each message that wants it.
  const [viewLimit, setViewLimit] = useState(0);
  const [viewOptionsOpen, setViewOptionsOpen] = useState(false);
  const viewOnce = viewLimit > 0;
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentions = useMentionAutocomplete({ value: text, onChange: setText, people: members });
  const desktop = useMediaQuery('(min-width: 768px)');
  const hasPhysicalKeyboard = useMediaQuery('(hover: hover) and (pointer: fine)');
  // The field's type: Instagram's 17px on a phone, 15px with a mouse. The mirror under it
  // carries the SAME metrics, or the caret drifts — so they are set once, here.
  const fontSize = bodySize(!desktop);
  const lineHeight = composerLineHeight(fontSize);
  const oneLine = lineHeight + 20;                 // one line plus the field's padding
  const maxHeight = composerMaxHeight(lineHeight);

  const [linkPreview, setLinkPreview] = useState(null);
  const [linkPreviewLoading, setLinkPreviewLoading] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const taRef = useRef(null);
  const mirrorRef = useRef(null);
  const replyBarRef = useRef(null);
  const debounceRef = useRef(null);

  const pickMention = (person) => {
    if (!person) return;
    const res = mentions.insert(person);
    if (!res) return;
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(res.caret, res.caret);
    });
  };
  useEffect(() => { setMentionIndex(0); }, [mentions.mention?.query]);

  const detectedUrl = useMemo(() => {
    const m = text.match(URL_REGEX);
    return m ? m[0] : null;
  }, [text]);

  useEffect(() => {
    if (!detectedUrl) { setLinkPreview(null); setLinkPreviewLoading(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLinkPreviewLoading(true);
    debounceRef.current = setTimeout(async () => {
      const r = await linkPreviewService.preview(detectedUrl);
      if (r.success) setLinkPreview(r.data);
      setLinkPreviewLoading(false);
    }, 600);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [detectedUrl]);

  // The reply bar rises in and the field takes focus.
  useLayoutEffect(() => {
    if (!replyTo) return;
    riseIn(replyBarRef.current, { dy: 12, ms: 220 });
    taRef.current?.focus();
  }, [replyTo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tellToast = (line) => { if (onToast) onToast(line); else setError(line); };

  /**
   * Measure a clip off a scratch <video>. Phone captures and webm often report Infinity until
   * seeked past the end, which is what the big seek is for. Resolves 0 when it can't tell.
   */
  const measureClip = (url) => new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata'; v.muted = true;
    let settled = false;
    const done = (sec) => {
      if (settled) return; settled = true;
      v.removeAttribute('src'); v.load();
      resolve(Number.isFinite(sec) && sec > 0 ? Math.round(sec * 1000) : 0);
    };
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) { done(v.duration); return; }
      v.ondurationchange = () => { if (Number.isFinite(v.duration) && v.duration > 0) done(v.duration); };
      try { v.currentTime = 1e101; } catch { /* the sheet can measure it */ }
    };
    v.onerror = () => done(0);
    setTimeout(() => done(Number.isFinite(v.duration) ? v.duration : 0), 4000);
    v.src = url;
  });

  /**
   * A pick — one file from the camera, up to ten from the gallery. The one validator for both
   * (albumModel.sortPick): photos 10 MB, videos 2 GB, ten at a time, anything else skipped
   * with a toast. A clip over 15 min is kept with its first 15 min as the window.
   */
  const acceptFiles = (fileList) => {
    setError('');
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const { kept, toasts } = sortPick(files, MAX_ALBUM_ITEMS - pendingItems.length);
    toasts.forEach(tellToast);
    if (!kept.length) return;
    const items = kept.map((file) => ({
      clientId: `pick:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      kind: file.type.startsWith('video/') ? 'video' : 'image',
      file, url: URL.createObjectURL(file), name: file.name, size: file.size,
      durationSec: 0, trim: null,
    }));
    setPendingItems((prev) => [...prev, ...items]);
    items.filter((i) => i.kind === 'video').forEach(async (i) => {
      const ms = await measureClip(i.url);
      setPendingItems((prev) => prev.map((q) => (q.clientId === i.clientId ? { ...q, durationSec: ms / 1000, trim: defaultTrim(ms) } : q)));
    });
  };
  const acceptFile = (file) => acceptFiles(file ? [file] : []);

  const removeItem = (clientId) => {
    setPendingItems((prev) => {
      const gone = prev.find((q) => q.clientId === clientId);
      if (gone) URL.revokeObjectURL(gone.url);
      const next = prev.filter((q) => q.clientId !== clientId);
      if (!next.length) { setViewLimit(0); setViewOptionsOpen(false); }
      return next;
    });
  };
  const removeAll = () => {
    pendingItems.forEach((q) => URL.revokeObjectURL(q.url));
    setPendingItems([]); setViewLimit(0); setViewOptionsOpen(false);
  };
  const makeCover = (clientId) => setPendingItems((prev) => {
    const x = prev.find((q) => q.clientId === clientId);
    return x ? [x, ...prev.filter((q) => q.clientId !== clientId)] : prev;
  });

  /** Hand off and clear. Everything after this — retry, offline hold — belongs to the outbox. */
  const submit = () => {
    if (!text.trim() && !pendingItems.length) return;
    setError('');
    if (pendingItems.length > 1) {
      // One album, cover first. The text under it goes as its own bubble, like a captioned photo.
      onSend({
        threadId, kind: 'album', viewOnce, viewLimit,
        items: pendingItems.map((p) => ({ clientId: p.clientId, kind: p.kind, file: p.file, previewUrl: p.url, name: p.name, size: p.size, durationSec: p.durationSec, trim: p.trim })),
      });
      setPendingItems([]);
    } else if (pendingImage?.kind === 'video') {
      onSend({ threadId, kind: 'video', file: pendingImage.file, previewUrl: pendingImage.url, filename: pendingImage.name, sizeBytes: pendingImage.size, durationSec: pendingImage.durationSec, trim: pendingImage.trim, viewOnce, viewLimit });
      setPendingItems([]);
    } else if (pendingImage) {
      onSend({ threadId, kind: 'image', file: pendingImage.file, previewUrl: pendingImage.url, filename: pendingImage.name, sizeBytes: pendingImage.size, viewOnce, viewLimit });
      setPendingItems([]);
    }
    const body = text.trim();
    if (body) {
      const quote = replyTo ? { id: replyTo.id, senderDisplayName: replyTo.senderDisplayName || replyTo.name, bodySnippet: replyTo.body } : null;
      const reply = replyTo ? { replyToMessageId: replyTo.id, quote } : {};
      // A view-once message stays words even when it holds a URL: no link card outlives the look.
      const asLink = !!detectedUrl && !viewOnce;
      onSend({
        threadId,
        kind: asLink ? 'link' : 'text',
        payload: asLink
          ? { kind: 'link', body, linkUrl: detectedUrl, ...reply }
          : { kind: 'text', body, viewOnce, viewLimit, ...reply },
      });
      setText('');
      setLinkPreview(null);
    }
    setViewLimit(0);
    setViewOptionsOpen(false);
    onCancelReply?.();
    // Stay ready for the next message: the field keeps focus (and the keyboard stays open)
    // until the person taps somewhere else.
    if (document.activeElement !== taRef.current) taRef.current?.focus({ preventScroll: true });
  };

  const sendGif = (gif) => {
    setGifOpen(false);
    onSend({ threadId, kind: 'gif', payload: { kind: 'gif', gifUrl: gif.url, gifLabel: gif.title || '', gifWidth: gif.width, gifHeight: gif.height } });
  };

  const onKey = (e) => {
    if (mentions.isOpen && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      pickMention(mentions.matches[mentionIndex] || mentions.matches[0]);
      return;
    }
    if (mentions.isOpen && e.key === 'Escape') { e.preventDefault(); mentions.close(); return; }
    if (mentions.isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setMentionIndex((i) => { const n = mentions.matches.length; return e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n; });
      return;
    }
    if (e.key === 'Escape' && replyTo) { onCancelReply?.(); return; }
    if (e.key === 'Enter' && enterSends({ hasPhysicalKeyboard, shiftKey: e.shiftKey })) {
      e.preventDefault();
      submit();
    }
  };

  // Grow with the text, to six lines, then scroll inside. Height is reset to auto first so
  // the field can shrink when a line is deleted.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return undefined;
    const fit = () => {
      ta.style.height = 'auto';
      const h = Math.max(oneLine, Math.min(ta.scrollHeight, maxHeight));
      ta.style.height = `${h}px`;
      ta.style.overflowY = ta.scrollHeight > maxHeight ? 'auto' : 'hidden';
      setMultiline(h > oneLine + 4);
    };
    fit();
    // Once more when the row beside the field has finished folding (240ms): a whole value
    // landing in the same frame as focus — a paste — was measured against a field still
    // mid-collapse, wrapped per character, and sat at six lines for fourteen characters.
    const again = setTimeout(fit, 280);
    return () => clearTimeout(again);
  }, [text, oneLine, maxHeight]);

  const { recording, seconds, start: startRec, stop: stopRec, cancel: cancelRec } = useVoiceRecorder({ onError: setError });

  const finishVoiceNote = async () => {
    const blob = await stopRec();
    if (!blob || blob.size < 1024 || seconds < 1) { setError(''); return; }
    // The waveform is measured here, from the recording itself; a browser that can't
    // decode its own format just sends without one and a listener's browser fills it in.
    const peaks = await measurePeaks(blob);
    onSend({ threadId, kind: 'audio', blob, durationSec: seconds, peaks, previewUrl: URL.createObjectURL(blob) });
  };

  // Phone: the handoff's rule (fold while the field is wanted). Desktop: the plan-detection bar's
  // rule — the four fold into a + only while there is text, so the field gets the width.
  const collapsed = desktop
    ? text.length > 0 && !actionsOpen
    : composerActionsCollapsed({ actionsOpen, focused, hasText: text.length > 0, gifOpen, emojiOpen });
  // PHOTO opens a two-row menu — CAMERA / GALLERY — instead of two icons.
  const [mediaOpen, setMediaOpen] = useState(false);
  useEffect(() => { onDraftChange?.(text.length > 0); }, [text, onDraftChange]);
  useEffect(() => { if (collapsed) setMediaOpen(false); }, [collapsed]);

  const insertEmoji = (emoji) => {
    const ta = taRef.current;
    if (!ta) { setText((t) => t + emoji); return; }
    const start = ta.selectionStart ?? text.length;
    const end = ta.selectionEnd ?? start;
    setText(text.slice(0, start) + emoji + text.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      const caret = start + emoji.length;
      ta.setSelectionRange(caret, caret);
    });
  };

  const canSend = !!text.trim() || pendingItems.length > 0;
  // The view-limit mark for words: beside SEND, and only once there is something typed — a
  // bare field has nothing to limit. The icon is the count in a portal ring; tapping it opens
  // the three choices. (A photo carries the full-text pill on its card instead.)
  const showTextLimit = !!text.trim() && !pendingItems.length;
  const viewLimitButton = showTextLimit && (
    <button
      type="button"
      data-testid="composer-view-limit"
      aria-haspopup="menu"
      aria-expanded={viewOptionsOpen}
      aria-label={viewOnce ? `${viewLimitLabel(viewLimit)} is on` : 'Limit how many times this can be viewed'}
      title={viewLimitLabel(viewLimit)}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => setViewOptionsOpen((o) => !o)}
      className="flex flex-shrink-0 items-center justify-center text-black"
      style={{ width: 36, height: 40, border: 0, padding: 0, background: 'transparent', cursor: 'pointer' }}
    >
      <ViewLimitIcon n={viewLimit || 1} on={viewOnce} muted={!viewOnce} size={28} />
    </button>
  );
  const fieldStyle = {
    display: 'flex', alignItems: 'flex-end', minWidth: 0, flex: 1, minHeight: oneLine,
    borderRadius: multiline ? RADIUS.bubble : RADIUS.pill,
    transition: 'border-radius 160ms linear',
    ...(focused
      ? discoFrame({ fill: T.white })
      : text.length > 0
        ? { border: '2px solid transparent', background: fillFor('open') }
        : { border: '2px solid transparent', background: T.off }),
  };
  if (focused) fieldStyle.borderRadius = multiline ? RADIUS.bubble : RADIUS.pill;

  const card = { background: T.off, borderRadius: RADIUS.row };

  if (noteMode) return <NoteComposer {...noteMode} />;

  return (
    <div className="composer-bar flex-shrink-0" style={{ background: T.white, position: 'relative' }}>
      {gifOpen && (
        <div className="max-h-[40vh] overflow-y-auto" style={{ padding: '8px 12px 0' }}>
          <GifPicker onPick={sendGif} onClose={() => setGifOpen(false)} />
        </div>
      )}
      {emojiOpen && <EmojiPicker onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />}

      {mentions.isOpen && (
        <div className="max-h-56 overflow-y-auto" style={{ margin: '8px 12px 0', ...card, overflow: 'hidden' }}>
          {mentions.matches.map((p, i) => (
            <button
              key={p.userId}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pickMention(p); }}
              onMouseEnter={() => setMentionIndex(i)}
              className="flex w-full items-center gap-3 px-3 py-2 text-left"
              style={{ background: i === mentionIndex ? T.dayPill : 'transparent' }}
            >
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden text-[11px] font-bold" style={{ borderRadius: RADIUS.small, background: T.activeBadge }}>
                {p.avatarUrl ? <img src={p.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : (p.displayName || '?').charAt(0).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm" style={{ fontFamily: BODY_FONT }}>{p.displayName}</span>
                {p.handle && <span className="block truncate font-mono text-[10px] uppercase tracking-wider" style={{ color: T.grey500 }}>@{p.handle}</span>}
              </span>
            </button>
          ))}
        </div>
      )}

      {trimming && (
        <VideoTrimSheet
          file={trimming.file}
          localUrl={trimming.url}
          onConfirm={(trim) => { setPendingItems((prev) => prev.map((q) => (q.clientId === trimming.clientId ? { ...q, trim } : q))); setTrimming(null); }}
          onCancel={() => setTrimming(null)}
        />
      )}

      {viewOptionsOpen && (
        <ViewLimitOptions
          what={pendingItems.length > 1 ? 'album' : pendingImage?.kind === 'video' ? 'video' : pendingImage ? 'photo' : 'message'}
          value={viewLimit}
          onPick={(v) => { setViewLimit(v); setViewOptionsOpen(false); }}
          onClose={() => setViewOptionsOpen(false)}
        />
      )}

      {replyTo && (
        <div ref={replyBarRef} data-replybar style={{ padding: '8px 12px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px 8px 14px', ...card }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>REPLYING TO {replyTo.name}</div>
              <div className="truncate" style={{ marginTop: 2, fontSize: 13, fontFamily: BODY_FONT }}>{replyTo.body}</div>
            </div>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onCancelReply}
              aria-label="Cancel reply"
              style={{ width: 28, height: 28, flexShrink: 0, border: 0, padding: 0, borderRadius: '50%', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      <div className="composer-row" style={{ padding: '8px 12px' }}>
        {pendingItems.length > 0 && (
          <AlbumTray
            items={pendingItems}
            viewLimit={viewLimit}
            viewOptionsOpen={viewOptionsOpen}
            onToggleViewOptions={() => setViewOptionsOpen((o) => !o)}
            onMakeCover={makeCover}
            onRemove={removeItem}
            onRemoveAll={removeAll}
            onTrim={(clientId) => { const it = pendingItems.find((q) => q.clientId === clientId); if (it) setTrimming(it); }}
            onAddMore={() => inputRef.current?.click()}
          />
        )}

        {error && (
          <div className="mb-2 flex items-center justify-between gap-2 font-mono text-[11px] font-bold uppercase tracking-wider" style={{ ...card, padding: '6px 12px', color: T.danger }}>
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} className="flex-shrink-0" style={{ borderRadius: RADIUS.pill, border: `2px solid ${T.danger}`, padding: '2px 8px' }}>Dismiss</button>
          </div>
        )}

        {detectedUrl && !pendingItems.length && (
          <div className="mb-2 flex gap-2 items-stretch" style={{ ...card, padding: 8 }}>
            {linkPreview?.imageUrl
              ? <img src={linkPreview.imageUrl} alt="" className="w-16 h-16 object-cover" style={{ borderRadius: RADIUS.small }} />
              : <div className="w-16 h-16" style={{ background: T.activeBadge, borderRadius: RADIUS.small }} />}
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[9px] font-bold tracking-widest" style={{ color: T.grey500 }}>
                {linkPreviewLoading ? 'FETCHING…' : (linkPreview?.siteName || detectedUrl).toUpperCase().slice(0, 50)} · LINK PREVIEW
              </div>
              <div className="text-[12px] font-bold leading-snug truncate" style={{ fontFamily: BODY_FONT }}>{linkPreview?.title || detectedUrl}</div>
              {linkPreview?.description && <div className="text-[11px] truncate" style={{ fontFamily: BODY_FONT, color: T.grey500 }}>{linkPreview.description}</div>}
            </div>
          </div>
        )}

        {recording ? (
          <div className="flex items-center gap-2" style={{ ...card, borderRadius: RADIUS.pill, padding: '6px 8px 6px 14px' }}>
            <span className="h-3 w-3 flex-shrink-0 animate-pulse rounded-full" style={{ background: T.danger }} aria-hidden />
            <span className="font-mono text-[11px] font-bold uppercase tracking-widest">Recording</span>
            <span className="font-mono text-[12px] tabular-nums tracking-wider">{formatDuration(seconds)}</span>
            {seconds >= MAX_VOICE_SECONDS && <span className="font-mono text-[10px] font-bold tracking-widest" style={{ color: T.grey500 }}>MAX · SEND IT</span>}
            <div className="flex-1" />
            <button type="button" onClick={() => { cancelRec(); setError(''); }} className="font-mono text-[10px] font-bold uppercase tracking-widest" style={{ borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, padding: '6px 12px', background: T.white }}>Cancel</button>
            <button type="button" onClick={finishVoiceNote} className="font-mono text-[10px] font-bold uppercase tracking-widest" style={{ borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, padding: '6px 12px', background: T.ink, color: T.white }}>Send</button>
          </div>
        ) : (
          <div className="flex items-end gap-1">
            {collapsed && (
              <button
                type="button"
                data-composer-expand
                // onMouseDown, not onClick: the field's blur fires first otherwise and the
                // row expands out from under the button being pressed.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setActionsOpen(true); setFocused(false); taRef.current?.blur(); }}
                aria-label="Show attachment options"
                style={{ width: 36, height: 36, flexShrink: 0, border: 0, padding: 0, borderRadius: '50%', background: T.white, color: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginRight: 4, alignSelf: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" strokeLinejoin="miter"><path d="M5 12h14M12 5v14" /></svg>
              </button>
            )}
            <div
              data-composer-actions
              className="flex flex-shrink-0 items-center overflow-hidden"
              style={{ maxWidth: collapsed ? 0 : 160, opacity: collapsed ? 0 : 1, transition: `max-width 240ms ${EASE.SLIDE}, opacity 160ms linear` }}
              aria-hidden={collapsed || undefined}
            >
              <ComposerAction label="Photo or video" active={mediaOpen} onClick={() => { setMediaOpen((o) => !o); setGifOpen(false); setEmojiOpen(false); }} tabIndex={collapsed ? -1 : 0} testId="composer-photo">
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="16" /><path d="M3 16l5-5 4 4 3-3 6 6" /><circle cx="8.5" cy="9.5" r="1.5" />
                </svg>
              </ComposerAction>
              <ComposerAction label="Record a voice note" onClick={startRec} tabIndex={collapsed ? -1 : 0}>
                <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
                </svg>
              </ComposerAction>
              <ComposerAction label="Send a GIF" active={gifOpen} onClick={() => { setGifOpen((o) => !o); setEmojiOpen(false); setMediaOpen(false); }} tabIndex={collapsed ? -1 : 0}>
                <span className="font-mono text-[12px] font-bold tracking-wider">GIF</span>
              </ComposerAction>
              {onFindPlan && (
                /* Lucide `calendar-plus` (a substitute, flagged in the plan-detection handoff). */
                <ComposerAction label="Find a plan in this chat" active={findPlanActive} onClick={() => { setMediaOpen(false); setGifOpen(false); setEmojiOpen(false); onFindPlan(); }} tabIndex={collapsed ? -1 : 0} testId="composer-find-plan">
                  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="square">
                    <rect x="3" y="5" width="18" height="16" /><path d="M3 10h18M8 3v4M16 3v4M12 13v5M9.5 15.5h5" />
                  </svg>
                </ComposerAction>
              )}
            </div>
            {mediaOpen && !collapsed && (
              <>
                <div aria-hidden onMouseDown={() => setMediaOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 35 }} />
                <div data-composer-media-menu role="menu" className="font-mono" style={{ position: 'absolute', left: 12, bottom: 62, zIndex: 36, width: 240, borderRadius: RADIUS.row, overflow: 'hidden', border: '2px solid transparent', background: fillFor('open') }}>
                  <button type="button" role="menuitem" onClick={() => { setMediaOpen(false); cameraRef.current?.click(); }} className="hover:bg-[#F2F2F2]" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: 0, background: 'none', fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: T.ink }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><path d="M3 8h4l2-3h6l2 3h4v12H3z" /><circle cx="12" cy="13" r="4" /></svg>CAMERA
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setMediaOpen(false); inputRef.current?.click(); }} className="hover:bg-[#F2F2F2]" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: 0, borderTop: `2px solid ${T.off}`, background: 'none', fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', color: T.ink }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><rect x="3" y="4" width="18" height="16" /><circle cx="9" cy="10" r="2" /><path d="M21 16l-5-5-9 9" /></svg>
                    <span style={{ flex: 1 }}>GALLERY</span><span style={{ fontSize: 9, color: T.grey500, fontWeight: 400 }}>UP TO {MAX_ALBUM_ITEMS}</span>
                  </button>
                </div>
              </>
            )}

            {/* CAMERA is one shot and appends; GALLERY takes up to ten at once. */}
            <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" hidden onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = ''; }} />
            <input ref={inputRef} type="file" accept="image/*,video/*" multiple hidden data-testid="composer-gallery-input" onChange={(e) => { acceptFiles(e.target.files); e.target.value = ''; }} />

            <div data-composer-field style={fieldStyle}>
              {/* Transparent text over a highlighted mirror, so a tag lights up as you type
                  it. Both layers carry the SAME box metrics, or the caret drifts. */}
              <div className="relative min-w-0 flex-1">
                <div
                  ref={mirrorRef}
                  aria-hidden
                  className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-black"
                  style={{ fontFamily: BODY_FONT, fontSize, lineHeight: `${lineHeight}px`, padding: '10px 14px' }}
                >
                  <MentionMirror text={text} />
                </div>
                <textarea
                  ref={taRef}
                  rows={1}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    mentions.sync(e.target.value, e.target.selectionStart);
                    if (e.target.value) notifyTyping(threadId);
                  }}
                  onKeyDown={onKey}
                  onKeyUp={(e) => mentions.sync(e.target.value, e.target.selectionStart)}
                  onClick={(e) => mentions.sync(e.target.value, e.target.selectionStart)}
                  onFocus={() => { setFocused(true); setActionsOpen(false); }}
                  onBlur={() => { setFocused(false); setTimeout(mentions.close, 120); }}
                  onScroll={(e) => { if (mirrorRef.current) mirrorRef.current.scrollTop = e.target.scrollTop; }}
                  placeholder={pendingItems.length ? 'Add a message' : viewOnce ? `${viewLimitLabel(viewLimit).charAt(0)}${viewLimitLabel(viewLimit).slice(1).toLowerCase()} message` : 'Message'}
                  enterKeyHint="enter"
                  className="pr-composer-field relative block w-full resize-none border-0 bg-transparent placeholder:text-gray-400 focus:outline-none focus:ring-0"
                  style={{
                    fontFamily: BODY_FONT,
                    fontSize,
                    lineHeight: `${lineHeight}px`,
                    padding: '10px 14px',
                    height: oneLine,
                    maxHeight,
                    scrollbarWidth: 'none',
                    color: 'transparent',
                    caretColor: '#000',
                  }}
                />
              </div>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setEmojiOpen((o) => !o); setGifOpen(false); }}
                aria-label="Emoji"
                className={`flex flex-shrink-0 items-center justify-center text-black ${emojiOpen ? 'opacity-100' : 'opacity-60 hover:opacity-100'}`}
                style={{ width: 32, height: 36, marginRight: 2 }}
              >
                <svg width={21} height={21} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9.5" />
                  <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
                  <path d="M9 9.5v.01M15 9.5v.01" />
                </svg>
              </button>
            </div>

            {viewLimitButton}
            <button
              ref={sendButtonRef}
              type="button"
              // preventDefault on mousedown keeps focus in the field: on iOS a plain tap blurs
              // the textarea first, the keyboard drops, the composer shifts under the finger and
              // the click never lands — so the first tap only closed the keyboard and a second
              // one was needed to send. The keyboard now stays up until the person taps away.
              onMouseDown={(e) => e.preventDefault()}
              onClick={submit}
              disabled={!canSend}
              aria-label="Send"
              title="Send"
              className="flex flex-shrink-0 items-center justify-center disabled:cursor-not-allowed"
              style={{
                width: 40, height: 40, borderRadius: '50%', border: 0, padding: 0,
                background: canSend ? T.ink : 'transparent', color: canSend ? T.white : T.ink, opacity: canSend ? 1 : 0.25,
                transition: 'background-color 200ms, color 200ms, opacity 200ms',
              }}
            >
              <svg width={23} height={23} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.5 2.5L11 13" />
                <path d="M21.5 2.5l-6.8 19-3.7-8.5L2.5 9.3z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** One action in the composer row — a bare icon in a 40×40 cell at 70%. */
/**
 * "Set how many times this can be viewed." — the three choices, the way Instagram lists them:
 * once, twice, unlimited, with a tick on the current one. A card above the composer, not a
 * modal: it belongs to the field it is setting.
 */
function ViewLimitOptions({ what, value, onPick, onClose }) {
  return (
    <div data-testid="view-limit-options" role="menu" aria-label="How many times this can be viewed" style={{ padding: '8px 12px 0' }}>
      <div style={{ background: T.off, borderRadius: RADIUS.row, padding: '10px 6px 6px' }}>
        <div className="flex items-center justify-between" style={{ padding: '0 10px 6px' }}>
          <span className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.12em', color: T.grey500 }}>SET HOW MANY TIMES THIS {what.toUpperCase()} CAN BE VIEWED</span>
          <button type="button" onClick={onClose} aria-label="Close" onMouseDown={(e) => e.preventDefault()} style={{ width: 24, height: 24, border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {VIEW_LIMITS.map((o) => {
          const picked = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              role="menuitemradio"
              aria-checked={picked}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(o.value)}
              className="flex w-full items-center gap-3 text-left font-mono font-bold"
              style={{ padding: '8px 10px', borderRadius: RADIUS.photo, background: picked ? T.white : 'transparent', fontSize: 11, letterSpacing: '.1em', color: T.ink }}
            >
              <ViewLimitIcon n={o.value || 1} check={o.value === 0} size={26} />
              <span className="flex-1">{o.label}</span>
              {picked && <span aria-hidden style={{ fontSize: 13 }}>✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The note field (voice-notes handoff §4; v2 Oct 2026; v3 Oct 22 2026). NOTE pinned the second
 * already, so the audio keeps playing while you type. Above the field: the pink `NOTE AT 0:10`
 * stamp (tap it to re-pin to now), the share switch — a lock and ONLY YOU, or the people glyph
 * and SHARED WITH REZA, which decides how the note is SAVED rather than being a second step
 * afterwards — and CANCEL. The field is a 16px pill (below 16px iOS zooms the page) ringed in
 * ink while note mode is on, with BOOKMARK while it is empty and SAVE once there are words;
 * Return saves, Shift+Return breaks the line. The pill, the switch and the button swallow
 * pointerdown so a tap on them never drops the keyboard.
 *
 * **It grows with the words**: a textarea, one line to {@link NOTE_MAX_LINES}, then it scrolls
 * inside — the whole note stays readable while it is written.
 *
 * **On a phone it is an overlay (`overlay`), not a row of the thread column.** In the column it
 * sat under the dock, and a dock with its notes drawer out plus the keyboard left no room: the
 * column overflowed and the note field was pushed off the bottom of the screen, under the
 * keyboard. Now it is portalled into <body> as a fixed layer — a scrim over the thread and the
 * dock, the bar on the keyboard — so it is over everything and always above the keyboard:
 * the native shell shrinks the WebView frame (`resize: 'native'`), and on the web
 * {@link useKeyboardInset} lifts it by what the keyboard covers. A tap on the scrim puts the
 * keyboard away; with nothing typed it is CANCEL.
 */
export const NOTE_LINE_HEIGHT = 22;        // 16px type on 22px lines
export const NOTE_MAX_LINES = 4;
export const NOTE_PAD_Y = 18;              // the field's vertical padding, top + bottom
export const NOTE_ONE_LINE = NOTE_LINE_HEIGHT + NOTE_PAD_Y;
export const NOTE_MAX_HEIGHT = NOTE_LINE_HEIGHT * NOTE_MAX_LINES + NOTE_PAD_Y;
/** The field's height for what it holds: one line at least, four at most, scrolling past that. */
export const noteFieldHeight = (scrollHeight) => Math.max(NOTE_ONE_LINE, Math.min(scrollHeight || 0, NOTE_MAX_HEIGHT));

export function NoteComposer({ stamp, at = 0, senderName = '', peerName = '', mine = false, onRepin, onSave, onCancel, saving = false, error = null, overlay = false }) {
  const [draft, setDraft] = useState('');
  const [shared, setShared] = useState(false);
  const [tall, setTall] = useState(false);
  const fieldRef = useRef(null);
  // Web only in effect: the native frame already ends at the keyboard, so this reads 0 there.
  const inset = useKeyboardInset(overlay);
  // Focus on mount: the thread already focused a hidden field inside the tap, so the keyboard
  // is up and this is a hand-over, which iOS allows.
  useEffect(() => { fieldRef.current?.focus({ preventScroll: true }); }, []);
  // Grow with the words, one line to four, then scroll inside.
  useLayoutEffect(() => {
    const ta = fieldRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const h = noteFieldHeight(ta.scrollHeight);
    ta.style.height = `${h}px`;
    ta.style.overflowY = (ta.scrollHeight || 0) > NOTE_MAX_HEIGHT ? 'auto' : 'hidden';
    setTall(h > NOTE_ONE_LINE + 4);
  }, [draft]);
  const pinned = stamp ?? Math.floor(at);
  const moved = Math.floor(at) !== pinned;
  const keepFocus = (e) => e.preventDefault();
  const save = () => { if (!saving) onSave?.(draft.trim(), shared); };
  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
    if (e.key === 'Escape') onCancel?.();
  };
  const them = firstNameOf(peerName) || (mine ? 'THEM' : firstNameOf(senderName)) || 'THEM';
  const spoken = them.charAt(0) + them.slice(1).toLowerCase();
  const bar = (
    <div data-testid="note-composer" data-overlay={overlay || undefined} className="composer-bar flex-shrink-0 flex flex-col" style={{ background: T.white, borderTop: `2px solid ${T.off}`, padding: '10px 12px', gap: 8 }}>
      <div className="flex items-center" style={{ gap: 8, padding: '0 4px' }}>
        <button
          type="button"
          onPointerDown={keepFocus}
          onClick={onRepin}
          aria-label={`Pinned at ${formatStamp(pinned)}. Tap to pin to now.`}
          data-testid="note-stamp"
          className="font-mono font-bold tabular-nums flex items-center flex-shrink-0"
          style={{ height: 22, padding: '0 8px', borderRadius: RADIUS.pill, border: 0, background: T.live, color: T.white, fontSize: 9, letterSpacing: '.1em', cursor: 'pointer' }}
        >
          NOTE AT {formatStamp(pinned)}
        </button>
        <button
          type="button"
          onPointerDown={keepFocus}
          onClick={() => setShared((v) => !v)}
          aria-pressed={shared}
          aria-label={shared ? `Shared with ${spoken}. Tap to keep it to yourself` : `Only you. Tap to share with ${spoken}`}
          data-testid="note-share"
          className="font-mono font-bold flex items-center"
          style={{ gap: 5, border: 0, background: 'transparent', padding: '2px 0', fontSize: 9, letterSpacing: '.1em', color: T.grey500, cursor: 'pointer' }}
        >
          {shared
            ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square" aria-hidden><circle cx="8" cy="9" r="3" /><circle cx="16" cy="9" r="3" /><path d="M2 20c0-3 3-5 6-5s6 2 6 5M14 15.3c.6-.2 1.3-.3 2-.3 3 0 6 2 6 5" /></svg>
            : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square" aria-hidden><rect x="5" y="11" width="14" height="10" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>}
          {shared ? `SHARED WITH ${them}` : 'ONLY YOU'}
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" onPointerDown={keepFocus} onClick={onCancel} className="font-mono font-bold" style={{ padding: '2px 0', border: 0, background: 'none', fontSize: 9, letterSpacing: '.1em', cursor: 'pointer' }}>CANCEL</button>
      </div>
      {/* The pill: its corners soften from a pill to 20px once the note has wrapped, like the message field. */}
      <div className="flex items-end" style={{ minHeight: 44, borderRadius: tall ? 20 : RADIUS.pill, background: T.off, padding: '2px 6px 2px 16px', gap: 6, outline: `2px solid ${T.ink}`, transition: 'border-radius 150ms' }}>
        <textarea
          ref={fieldRef}
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder="Write a note, or save a bookmark"
          enterKeyHint="done"
          aria-label="Note"
          data-testid="note-field"
          className="flex-1 min-w-0 block resize-none border-0 bg-transparent outline-none focus:outline-none focus:ring-0 placeholder:text-gray-400"
          style={{ height: NOTE_ONE_LINE, maxHeight: NOTE_MAX_HEIGHT, padding: `${NOTE_PAD_Y / 2}px 0`, fontFamily: BODY_FONT, fontSize: 16, lineHeight: `${NOTE_LINE_HEIGHT}px`, color: T.ink, scrollbarWidth: 'none' }}
        />
        <button
          type="button"
          onPointerDown={keepFocus}
          onClick={save}
          disabled={saving}
          data-testid="note-save"
          className="font-mono font-bold flex-shrink-0 disabled:opacity-50"
          style={{ height: 32, marginBottom: 4, padding: '0 14px', borderRadius: RADIUS.pill, border: 0, background: T.ink, color: T.white, fontSize: 10, letterSpacing: '.1em', cursor: 'pointer' }}
        >
          {draft.trim() ? 'SAVE' : 'BOOKMARK'}
        </button>
      </div>
      {(error || moved) && (
        <span className="font-mono" data-testid="note-hint" style={{ fontSize: 9, letterSpacing: '.1em', color: error ? T.danger : T.grey400 }}>
          {error || `IT KEEPS PLAYING · TAP AT ${formatStamp(pinned)} TO PIN TO ${formatStamp(at)}`}
        </span>
      )}
    </div>
  );
  if (!overlay || typeof document === 'undefined') return bar;
  // A tap on the scrim puts the keyboard away and keeps the words; with nothing typed it cancels.
  const scrimTap = () => { if (draft.trim()) fieldRef.current?.blur(); else onCancel?.(); };
  return createPortal(
    <div className="pr-overlay-root" data-testid="note-overlay" style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
      <div aria-hidden onClick={scrimTap} data-testid="note-scrim" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.28)' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: inset, boxShadow: '0 -8px 24px rgba(0,0,0,.12)' }}>{bar}</div>
    </div>,
    document.body,
  );
}

function ComposerAction({ label, onClick, active = false, children, tabIndex, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      tabIndex={tabIndex}
      data-testid={testId}
      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center transition-opacity ${active ? 'text-black' : 'text-black opacity-70 hover:opacity-100'}`}
    >
      {children}
    </button>
  );
}

function GifPicker({ onPick, onClose }) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [gridWidth, setGridWidth] = useState(320);
  const containerRef = useRef(null);

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(tid);
  }, [query]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const measure = () => {
      const w = containerRef.current?.clientWidth;
      if (w && w > 0) setGridWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const fetchGifs = useCallback(
    (offset) => (debouncedQuery
      ? giphyFetch.search(debouncedQuery, { offset, limit: 12, rating: 'pg-13' })
      : giphyFetch.trending({ offset, limit: 12, rating: 'pg-13' })),
    [debouncedQuery],
  );

  const handleGifClick = (gif, e) => {
    e?.preventDefault?.();
    onPick({
      id: String(gif.id),
      url: gif.images?.downsized_medium?.url || gif.images?.original?.url,
      title: gif.title || '',
      width: gif.images?.original?.width || 0,
      height: gif.images?.original?.height || 0,
    });
  };

  return (
    <div style={{ background: T.off, borderRadius: RADIUS.row, padding: 10 }}>
      <div className="flex justify-between items-center mb-2">
        <span className="font-mono text-[10px] font-bold tracking-widest" style={{ color: T.grey500 }}>GIF SEARCH · GIPHY</span>
        <button type="button" onClick={onClose} className="font-mono text-[10px] font-bold tracking-widest">CLOSE</button>
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="SEARCH GIFS…"
        autoFocus
        className="w-full font-mono text-xs uppercase tracking-wider mb-2 border-0 focus:outline-none focus:ring-0"
        style={{ borderRadius: RADIUS.pill, background: T.white, padding: '8px 14px' }}
      />
      <div ref={containerRef} className="max-h-60 overflow-y-auto" style={{ borderRadius: 12 }}>
        <Grid key={debouncedQuery} fetchGifs={fetchGifs} width={gridWidth} columns={3} gutter={4} hideAttribution noLink onGifClick={handleGifClick} />
      </div>
    </div>
  );
}
